import { useEffect, useRef, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'

import { Box, Text } from '@chakra-ui/react'

import {
  DarkProvider,
  Modal,
  Tag,
  TagGroup,
  TypeaheadSearch,
} from '@hivewatch/dark'

import {
  useAddTagsToDeviceMutation,
  useCreateTagAndAddToDeviceMutation,
  useRemoveTagsFromDeviceMutation,
  useTagsLazyQuery,
} from '@/graphql/generated/hooks'
import {
  LABEL_DEVICE_DRAWER_ADD_FAILURE,
  LABEL_DEVICE_DRAWER_ADD_SUCCESS,
  LABEL_DEVICE_DRAWER_CREATE_FAILURE,
  LABEL_DEVICE_DRAWER_CREATE_SUCCESS,
  LABEL_DEVICE_DRAWER_OPEN_PICKER,
  LABEL_DEVICE_DRAWER_REMOVE_FAILURE,
  LABEL_DEVICE_DRAWER_REMOVE_SUCCESS,
  LABEL_WARNING_MAX_LENGTH,
  LABEL_WARNING_TOO_LONG,
  trackEvent,
} from '@/utils/analytics'

import {
  popupBodyStyleOverrides,
  popupHeaderStyleOverrides,
} from './DeviceLabelsSection.styles'

const DEBOUNCE_MS = 300

interface DeviceTagI {
  id: string
  name: string
  enabled: boolean
}

interface TagSearchInputIProps {
  deviceTagIds: Set<string>
  deviceTagNames: Set<string>
  onClose: () => void
  onAdd: (args: {
    tagId: string
    dropdownPosition: number
    searchTerm: string
  }) => void
  onCreate: (tagName: string) => void
  onLabelQueryChange?: (q: string) => void
}

const TagSearchInput = ({
  deviceTagIds,
  deviceTagNames,
  onClose,
  onAdd,
  onCreate,
  onLabelQueryChange,
}: TagSearchInputIProps) => {
  const colorScheme = useLightMode()
  const [fetchTags, { data, previousData, loading: isLoading }] =
    useTagsLazyQuery({
      fetchPolicy: 'network-only',
    })
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [query, setQuery] = useState('')

  useEffect(() => {
    trackEvent(LABEL_DEVICE_DRAWER_OPEN_PICKER)
  }, [])

  useEffect(() => {
    fetchTags({
      variables: { filter: { enabled: true, archived: false }, limit: 10 },
    })
  }, [fetchTags])

  const handleQueryChange = (q: string) => {
    setQuery(q)
    const trimmed = q.trim()
    onLabelQueryChange?.(trimmed)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!trimmed) {
      fetchTags({
        variables: { filter: { enabled: true, archived: false }, limit: 10 },
      })
      return
    }
    debounceRef.current = setTimeout(() => {
      fetchTags({
        variables: {
          filter: { enabled: true, archived: false, nameLike: trimmed },
          limit: 10,
        },
      })
    }, DEBOUNCE_MS)
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const options = ((data ?? previousData)?.tags?.edges ?? [])
    .map((e) => e.node)
    .filter((tag) => !deviceTagIds.has(tag.id))
    .map((tag) => ({ value: tag.id, label: tag.name }))

  const normalizedQuery = query.trim()

  const hasExactMatch =
    !!normalizedQuery &&
    options.some(
      (opt) =>
        (opt.label ?? opt.value).toLowerCase() === normalizedQuery.toLowerCase()
    )

  const isMatchingDeviceTag =
    !!normalizedQuery && deviceTagNames.has(normalizedQuery.toLowerCase())

  const shouldHideCreateAction =
    !normalizedQuery || hasExactMatch || isMatchingDeviceTag

  return (
    <TypeaheadSearch
      autoFocus
      colorScheme={colorScheme}
      height={28}
      inline={false}
      maxCharacter={50}
      maxDropdownHeight={220}
      maxResults={10}
      noMatchLabel={isMatchingDeviceTag ? '' : undefined}
      noOptionsLabel={isLoading ? undefined : 'All labels assigned'}
      onDismiss={onClose}
      onQueryChange={handleQueryChange}
      onSelect={(tagId) => {
        const dropdownPosition = options.findIndex(
          (option) => option.value === tagId
        )
        onAdd({
          dropdownPosition,
          searchTerm: normalizedQuery,
          tagId,
        })
        onClose()
      }}
      options={isMatchingDeviceTag ? [] : options}
      placeholder='Search labels...'
      primaryAction={
        shouldHideCreateAction
          ? undefined
          : {
              header: 'Create new label',
              label: (q) => `+ ${q.trim()}`,
              onClick: (q) => {
                onCreate(q.trim())
                onClose()
              },
            }
      }
      resultsHeader='Select label'
      showIcon={false}
      showResultsWhenEmpty={!isMatchingDeviceTag}
    />
  )
}

interface IProps {
  deviceId: string
  deviceTags: DeviceTagI[]
  editable: boolean
}

const MAX_LABEL_CHARS = 50
const TOO_LONG_THRESHOLD = MAX_LABEL_CHARS - 10

export const DeviceLabelsSection = ({
  deviceId,
  deviceTags,
  editable,
}: IProps) => {
  const colorScheme = useLightMode()
  const [addTagsToDevice] = useAddTagsToDeviceMutation()
  const [removeTagsFromDevice] = useRemoveTagsFromDeviceMutation()
  const [createTagAndAddToDevice] = useCreateTagAndAddToDeviceMutation()
  const [labelQuery, setLabelQuery] = useState('')
  const [pendingRemoveTag, setPendingRemoveTag] = useState<DeviceTagI | null>(
    null
  )
  const { showError } = useToasts()

  const deviceTagIds = new Set(deviceTags.map((t) => t.id))
  const deviceTagNames = new Set(deviceTags.map((t) => t.name.toLowerCase()))

  const isNearLimit = labelQuery.length >= TOO_LONG_THRESHOLD

  useEffect(() => {
    if (!labelQuery) return

    if (
      labelQuery.length >= TOO_LONG_THRESHOLD &&
      labelQuery.length < MAX_LABEL_CHARS
    ) {
      trackEvent(LABEL_WARNING_TOO_LONG, {
        name: labelQuery,
        surface: 'device_drawer_labels',
      })
    }

    if (labelQuery.length === MAX_LABEL_CHARS) {
      trackEvent(LABEL_WARNING_MAX_LENGTH, {
        name: labelQuery,
        surface: 'device_drawer_labels',
      })
    }
  }, [labelQuery])

  const addInput = editable
    ? (onClose: () => void) => (
        <TagSearchInput
          deviceTagIds={deviceTagIds}
          deviceTagNames={deviceTagNames}
          onAdd={async ({ tagId, dropdownPosition, searchTerm }) => {
            try {
              await addTagsToDevice({
                variables: { input: { deviceId, tagIds: [tagId] } },
              })
              trackEvent(LABEL_DEVICE_DRAWER_ADD_SUCCESS, {
                device_id: deviceId,
                dropdown_position: dropdownPosition,
                label_id: tagId,
                search_term: searchTerm,
                surface: 'device_drawer_labels',
              })
            } catch (error) {
              Sentry.captureException(error, {
                tags: {
                  action: 'add',
                  feature: 'device_labels',
                  surface: 'device_drawer_labels',
                },
                extra: {
                  deviceId,
                  tagId,
                },
              })
              trackEvent(LABEL_DEVICE_DRAWER_ADD_FAILURE, {
                device_id: deviceId,
                dropdown_position: dropdownPosition,
                label_id: tagId,
                search_term: searchTerm,
                surface: 'device_drawer_labels',
              })
              showError('Failed to add label. Please try again.')
            }
          }}
          onClose={() => {
            setLabelQuery('')
            onClose()
          }}
          onCreate={async (tagName) => {
            try {
              const response = await createTagAndAddToDevice({
                variables: { input: { deviceId, tagName } },
              })
              const createdLabelId =
                response.data?.createTagAndAddToDevice?.device?.tags?.edges
                  ?.map((edge) => edge.node)
                  ?.find((tag) => tag.name === tagName)?.id
              trackEvent(LABEL_DEVICE_DRAWER_CREATE_SUCCESS, {
                created_label_id: createdLabelId,
                device_id: deviceId,
                label_name: tagName,
                surface: 'device_drawer_labels',
              })
            } catch (error) {
              Sentry.captureException(error, {
                tags: {
                  action: 'create',
                  feature: 'device_labels',
                  surface: 'device_drawer_labels',
                },
                extra: {
                  deviceId,
                  tagName,
                },
              })
              trackEvent(LABEL_DEVICE_DRAWER_CREATE_FAILURE, {
                device_id: deviceId,
                label_name: tagName,
                surface: 'device_drawer_labels',
              })
              showError('Failed to create label. Please try again.')
            }
          }}
          onLabelQueryChange={setLabelQuery}
        />
      )
    : undefined

  return (
    <Box
      borderBottomColor={`border.subtle.${colorScheme}`}
      borderBottomStyle='solid'
      borderBottomWidth='1px'
      px='24px'
      py='16px'
    >
      <Text fontFamily='Soehne' fontWeight='bold' mb='8px'>
        Labels
      </Text>
      {isNearLimit && (
        <Box color='red.400' fontSize='12px' mb='8px'>
          This label is pretty long. You may want to break this into multiple
          labels.
        </Box>
      )}
      <DarkProvider>
        <TagGroup
          addInput={addInput}
          colorScheme={colorScheme}
          editable={editable}
          wrap
        >
          {deviceTags.map((tag) => (
            <Tag
              colorScheme={colorScheme}
              isArchived={!tag.enabled}
              key={tag.id}
              value={tag.name}
              {...(editable && {
                editable: true,
                onRemove: tag.enabled
                  ? async () => {
                      try {
                        await removeTagsFromDevice({
                          variables: { input: { deviceId, tagIds: [tag.id] } },
                        })
                        trackEvent(LABEL_DEVICE_DRAWER_REMOVE_SUCCESS, {
                          device_id: deviceId,
                          label_id: tag.id,
                          label_name: tag.name,
                          surface: 'device_drawer_labels',
                          tag_enabled: true,
                        })
                      } catch (error) {
                        Sentry.captureException(error, {
                          tags: {
                            action: 'remove',
                            feature: 'device_labels',
                            surface: 'device_drawer_labels',
                          },
                          extra: {
                            deviceId,
                            tagEnabled: true,
                            tagId: tag.id,
                            tagName: tag.name,
                          },
                        })
                        trackEvent(LABEL_DEVICE_DRAWER_REMOVE_FAILURE, {
                          device_id: deviceId,
                          label_id: tag.id,
                          label_name: tag.name,
                          surface: 'device_drawer_labels',
                          tag_enabled: true,
                        })
                        showError('Failed to remove label. Please try again.')
                      }
                    }
                  : () => {
                      setPendingRemoveTag(tag)
                    },
              })}
            />
          ))}
        </TagGroup>
      </DarkProvider>
      <Modal
        bodyStyleOverrides={popupBodyStyleOverrides}
        colorScheme={colorScheme}
        headerStyleOverrides={popupHeaderStyleOverrides}
        isOpen={pendingRemoveTag !== null}
        onClose={() => setPendingRemoveTag(null)}
        onSave={async () => {
          if (!pendingRemoveTag) return
          try {
            await removeTagsFromDevice({
              variables: {
                input: { deviceId, tagIds: [pendingRemoveTag.id] },
              },
            })
            trackEvent(LABEL_DEVICE_DRAWER_REMOVE_SUCCESS, {
              device_id: deviceId,
              label_id: pendingRemoveTag.id,
              label_name: pendingRemoveTag.name,
              surface: 'device_drawer_labels',
              tag_enabled: false,
            })
          } catch (error) {
            Sentry.captureException(error, {
              tags: {
                action: 'remove',
                feature: 'device_labels',
                surface: 'device_drawer_labels',
              },
              extra: {
                deviceId,
                tagEnabled: false,
                tagId: pendingRemoveTag.id,
                tagName: pendingRemoveTag.name,
              },
            })
            trackEvent(LABEL_DEVICE_DRAWER_REMOVE_FAILURE, {
              device_id: deviceId,
              label_id: pendingRemoveTag.id,
              label_name: pendingRemoveTag.name,
              surface: 'device_drawer_labels',
              tag_enabled: false,
            })
            showError('Failed to remove label. Please try again.')
          }
          setPendingRemoveTag(null)
        }}
        saveButtonLabel='Remove label'
        size='compact'
        title='Are you sure?'
        zIndex={1500}
      >
        This label is disabled and you won&#x2019;t be able to add it again
      </Modal>
    </Box>
  )
}
